import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, UsersThree } from "@phosphor-icons/react";

const TONES = {
  teal: "border-teal-200 bg-teal-50 text-teal-900",
  green: "border-green-200 bg-green-50 text-green-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  slate: "border-slate-200 bg-slate-50 text-slate-900",
};

function slugify(label) {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export default function JobWizardRoleAssignments({
  roles,
  users,
  assignments,
  onAssignmentsChange,
  onRolesChange,
}) {
  const [newRole, setNewRole] = useState("");
  const toggleMember = (roleKey, userId) => {
    const selected = assignments[roleKey] || [];
    const next = selected.includes(userId)
      ? selected.filter((id) => id !== userId)
      : [...selected, userId];
    onAssignmentsChange({ ...assignments, [roleKey]: next });
  };
  const addCustomRole = () => {
    const label = newRole.trim();
    const baseKey = slugify(label);
    if (!baseKey) return;
    const existingKeys = new Set(roles.map((role) => role.key));
    const key = existingKeys.has(baseKey) ? `${baseKey}_${Date.now()}` : baseKey;
    onRolesChange([...roles, { key, label, short: "CUSTOM", tone: "slate", custom: true }]);
    setNewRole("");
  };

  return (
    <section className="space-y-4" data-testid="job-wizard-role-assignment-editor">
      <div className="border border-amber-200 bg-amber-50 p-4">
        <h2 className="font-heading text-base font-bold text-amber-950">
          Step 4: Incharge Roles & Drawing Target
        </h2>
        <p className="mt-1 text-xs text-amber-800">
          Select one or more team members for each role. Add project-specific incharge roles when
          the standard four do not cover the job.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {roles.map((role) => {
          const current = assignments[role.key];
          const selected = Array.isArray(current) ? current : current ? [current] : [];
          return (
            <div
              key={role.key}
              className={`border p-4 ${TONES[role.tone] || TONES.slate}`}
              data-testid={`job-role-card-${role.key}`}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-sm font-bold">{role.label}</div>
                  <div className="mt-0.5 text-[10px] font-medium opacity-70">
                    {selected.length} member{selected.length === 1 ? "" : "s"} selected
                  </div>
                </div>
                <UsersThree size={19} className="shrink-0 opacity-70" />
              </div>
              <div className="max-h-40 space-y-1 overflow-y-auto border border-white/80 bg-white/80 p-2">
                {users.map((user) => (
                  <label
                    key={user.id}
                    className="flex cursor-pointer items-center gap-2 px-1 py-1.5 text-xs text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(user.id)}
                      onChange={() => toggleMember(role.key, user.id)}
                      className="h-3.5 w-3.5 accent-emerald-600"
                      data-testid={`job-role-member-${role.key}-${user.id}`}
                    />
                    <span className="min-w-0 flex-1 truncate font-semibold">{user.name}</span>
                    <span className="max-w-24 truncate text-[10px] text-slate-500">
                      {user.department || user.role}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="border border-slate-200 bg-white p-4" data-testid="job-custom-role-control">
        <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-600">
          Add custom incharge role
        </label>
        <div className="flex flex-wrap gap-2">
          <Input
            value={newRole}
            onChange={(event) => setNewRole(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && addCustomRole()}
            placeholder="e.g. Quality Incharge"
            className="h-9 max-w-md border-slate-200"
            data-testid="job-custom-role-input"
          />
          <Button
            type="button"
            onClick={addCustomRole}
            disabled={!newRole.trim()}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="job-custom-role-add"
          >
            <Plus size={15} /> Add role
          </Button>
        </div>
      </div>
    </section>
  );
}